import { DEFAULT_RELATIVE_PAST_DATE_OPTIONS } from 'components/Common/DateRangeFilter/constants';
import { calculateDateTimeForRelativeDate } from 'components/Common/DateRangeFilter/utils';
import { FILTER_SETS } from 'components/Common/VerticalFilterV2/constants';

import dayjs from 'services/dayjs';

import {
  DEFAULT_DATE_RANGE,
  DEFAULT_PAST_DATE_RANGE,
  EVENT_LIST_VIEW_TYPE,
} from 'modules/EventSchedulerV2/constants';
import {
  SET_SHOW_DISTRICTS_WITHOUT_EVENTS,
  UPDATE_EVENT_OPENINGS_SHARED_FILTERS,
  UPDATE_ALL_DISTRICTS_EVENTS_FILTERS,
  UPDATE_HOST_EVENTS_FILTERS,
  UPDATE_K12_ADMIN_EVENTS_FILTERS,
  UPDATE_MEMBER_EVENTS_VIEW_TYPE,
  UPDATE_MEMBER_EVENT_DRAWER_ID,
  UPDATE_STAFF_EVENTS_VIEW_TYPE,
  UPDATE_STAFF_PARTNER_EVENTS_VIEW_TYPE,
  SET_SCHEDULED_EVENTS_SELECTED_EVENT_ID,
  SET_PARTNER_EVENTS_SELECTED_EVENT_ID,
  SET_CLEARED_SUGGESTED_EVENTS,
} from 'modules/EventSchedulerV2/store/actionTypes';

const { startDate, endDate } = calculateDateTimeForRelativeDate({
  relativeDateId: DEFAULT_DATE_RANGE,
});

const { startDate: pastStartDate, endDate: pastEndDate } =
  calculateDateTimeForRelativeDate({
    relativeDateId: DEFAULT_PAST_DATE_RANGE,
    relativeDateOptions: DEFAULT_RELATIVE_PAST_DATE_OPTIONS,
  });

const K12_ADMIN_EVENTS_INITIAL_STATE = {
  [FILTER_SETS.SCHEDULED_EVENTS]: {
    startDatetime: {
      date: startDate,
      time: dayjs(),
    },
    endDatetime: {
      date: endDate,
      time: dayjs().endOf('day'),
    },
  },
  [FILTER_SETS.PARTNER_EVENTS]: {
    startDatetime: {
      date: startDate,
      time: dayjs(),
    },
    endDatetime: {
      date: endDate,
      time: dayjs().endOf('day'),
    },
  },
  [FILTER_SETS.PAST_EVENTS]: {
    startDatetime: {
      date: pastStartDate,
      time: dayjs().startOf('day'),
    },
    endDatetime: {
      date: pastEndDate,
      time: dayjs(),
    },
  },
  scheduledEventsView: EVENT_LIST_VIEW_TYPE.List,
  partnerEventsView: EVENT_LIST_VIEW_TYPE.List,
  scheduledEventsSelectedEventId: null,
  partnerEventsSelectedEventId: null,
};

/** Default date range filter will be ALL_TIME, since startDate and endDate is null for both the eventOpenings and hostEvents */
const MEMBER_EVENTS_INITIAL_STATE = {
  eventOpenings: {
    sharedFilters: {
      startDate: null,
      endDate: null,
      type: [],
      format: [],
      startTime: null,
      endTime: null,
    },
    allDistrictsEventsFilters: {
      state: [],
      radius: null,
      address: null, // it represents the location object which contains zipCode as well
      territoryFilter: null,
    },
    showDistrictsWithoutEvents: false,
    selectedView: EVENT_LIST_VIEW_TYPE.List,
    eventDrawerId: null, // it is used to open EventDrawer with the given event hash_id.
  },
  hostEvents: {
    startDate: null,
    endDate: null,
    type: [],
    format: [],
  },
  memberDashboard: {
    clearedSuggestedEvents: [],
  },
};

const initialState = {
  member: MEMBER_EVENTS_INITIAL_STATE,
  k12Admin: K12_ADMIN_EVENTS_INITIAL_STATE,
};

const eventSchedulerReducer = (state = initialState, action) => {
  const { type, payload } = action;

  switch (type) {
    case UPDATE_ALL_DISTRICTS_EVENTS_FILTERS: {
      return {
        ...state,
        member: {
          ...state.member,
          eventOpenings: {
            ...state.member.eventOpenings,
            allDistrictsEventsFilters: {
              ...state.member.eventOpenings.allDistrictsEventsFilters,
              ...payload,
            },
          },
        },
      };
    }
    case UPDATE_EVENT_OPENINGS_SHARED_FILTERS: {
      return {
        ...state,
        member: {
          ...state.member,
          eventOpenings: {
            ...state.member.eventOpenings,
            sharedFilters: {
              ...state.member.eventOpenings.sharedFilters,
              ...payload,
            },
          },
        },
      };
    }
    case SET_SHOW_DISTRICTS_WITHOUT_EVENTS: {
      return {
        ...state,
        member: {
          ...state.member,
          eventOpenings: {
            ...state.member.eventOpenings,
            showDistrictsWithoutEvents: payload,
          },
        },
      };
    }
    case UPDATE_HOST_EVENTS_FILTERS: {
      return {
        ...state,
        member: {
          ...state.member,
          hostEvents: {
            ...state.member.hostEvents,
            ...payload,
          },
        },
      };
    }
    case UPDATE_K12_ADMIN_EVENTS_FILTERS:
      return {
        ...state,
        k12Admin: {
          ...state.k12Admin,
          [payload.filterStateKey]: {
            ...state.k12Admin[payload.filterStateKey],
            ...payload.value,
          },
        },
      };
    case UPDATE_MEMBER_EVENTS_VIEW_TYPE:
      return {
        ...state,
        member: {
          ...state.member,
          eventOpenings: {
            ...state.member.eventOpenings,
            selectedView: payload,
          },
        },
      };
    case UPDATE_MEMBER_EVENT_DRAWER_ID:
      return {
        ...state,
        member: {
          ...state.member,
          eventOpenings: {
            ...state.member.eventOpenings,
            eventDrawerId: payload,
          },
        },
      };
    case SET_CLEARED_SUGGESTED_EVENTS:
      return {
        ...state,
        member: {
          ...state.member,
          memberDashboard: {
            ...state.member.memberDashboard,
            clearedSuggestedEvents: payload,
          },
        },
      };
    case UPDATE_STAFF_EVENTS_VIEW_TYPE:
      return {
        ...state,
        k12Admin: {
          ...state.k12Admin,
          scheduledEventsView: payload,
        },
      };
    case UPDATE_STAFF_PARTNER_EVENTS_VIEW_TYPE:
      return {
        ...state,
        k12Admin: {
          ...state.k12Admin,
          partnerEventsView: payload,
        },
      };
    case SET_SCHEDULED_EVENTS_SELECTED_EVENT_ID:
      return {
        ...state,
        k12Admin: {
          ...state.k12Admin,
          scheduledEventsSelectedEventId: payload,
        },
      };
    case SET_PARTNER_EVENTS_SELECTED_EVENT_ID:
      return {
        ...state,
        k12Admin: {
          ...state.k12Admin,
          partnerEventsSelectedEventId: payload,
        },
      };
    default:
      return state;
  }
};

export default eventSchedulerReducer;
